import { NextPage } from "next";
import Head from "next/head";

import Section from "@/components/Section2";
import Link from "next/link";
import clsx from "clsx";
import { useAtom } from "jotai";
import {
  footerHeight,
  headerHeight,
  scrollYAfterHeaderAtom,
  scrollYAtom,
  scrollYBeforeFooterAtom,
} from "@/data/hooks/useScrollY";

import { HTMLAttributes, useEffect } from "react";
import { kuberHeadTitle } from "@/data/constants";

const PrivacyPolicyPage: NextPage = () => {
  const [scrollY] = useAtom(scrollYAtom);
  const [scrollYAfterHeader, setScrollYAfterHeader] = useAtom(scrollYAfterHeaderAtom);
  const [scrollYBeforeFooter, setScrollYBeforeFooter] = useAtom(scrollYBeforeFooterAtom);

  useEffect(() => {
    const containerHeight = document.getElementById("privacy-context")?.offsetHeight;
    const endScrollYPoint = containerHeight ? containerHeight - footerHeight : 0;
    const middleScrollYPoint = containerHeight ? containerHeight - (containerHeight - headerHeight) : 0;
    setScrollYBeforeFooter(endScrollYPoint);
    setScrollYAfterHeader(middleScrollYPoint);
  }, []);

  return (
    <>
      <Head>
        <title>Политика обработки и&nbsp;защиты персональных данных | {kuberHeadTitle}</title>
      </Head>
      <Section>
        <div className="max-w-xxl gap-8 pb-4 pt-12 desktop:flex desktop:flex-row desktop:pt-16">
          <div
            className="flex w-full flex-col gap-10 text-textBody tablet:justify-start desktop:basis-2/3"
            id="privacy-context"
          >
            <h2 className="text-header2 desktop:text-header1">
              Политика обработки и&nbsp;защиты персональных данных ООО &laquo;КУБЕР&raquo;
            </h2>
            <div className="flex flex-col gap-4 pb-4" id="general">
              <h2 className="text-title1">1.&nbsp;Общие положения</h2>
              <div className="flex flex-col gap-4">
                <ListItem section="1.1." doubleDigit={true}>
                  <span>
                    Настоящая политика оператора в&nbsp;отношении обработки и&nbsp;защиты персональных данных (далее
                    по&nbsp;тексту&nbsp;&mdash; Политика) разработана в&nbsp;соответствии с&nbsp;положениями Конституции
                    Российской Федерации (далее по&nbsp;тексту&nbsp;&mdash; РФ), Федерального закона от&nbsp;27.07.2006
                    &#8470;&nbsp;149-ФЗ &laquo;Об&nbsp;информации, информационных технологиях и&nbsp;о&nbsp;защите
                    информации&raquo;, Федерального закона от&nbsp;27.07.2006 &#8470;&nbsp;152-ФЗ
                    &laquo;О&nbsp;персональных данных&raquo; (далее&nbsp;&mdash; Закон о&nbsp;персональных данных)
                    и&nbsp;иными нормативными правовыми актами в&nbsp;области защиты и&nbsp;обработки персональных
                    данных, действующими на&nbsp;территории&nbsp;РФ, а&nbsp;также во&nbsp;исполнение таких нормативных
                    правовых актов.
                  </span>
                </ListItem>
                <ListItem section="1.2." doubleDigit={true}>
                  <span>В&nbsp;настоящей Политике используются следующие понятия:</span>
                </ListItem>
                <div className="flex flex-col gap-4 pl-10">
                  <p>
                    <span className="font-semibold">
                      Оператор персональных данных <span className="font-normal">или</span> Оператор&nbsp;&mdash;&nbsp;
                    </span>
                    юридическое лицо Общество с&nbsp;ограниченной ответственностью &laquo;КУБЕР&raquo; (ОГРН
                    1207700248183, ИНН 7722489721), самостоятельно или совместно с&nbsp;другими лицами
                    организующее&nbsp;и (или) осуществляющее обработку персональных данных, а&nbsp;также определяющее
                    цели обработки персональных данных, состав персональных данных, подлежащих обработке, действия
                    (операции), совершаемые с&nbsp;персональными данными.
                  </p>
                  <p>
                    <span className="font-semibold">Сайт — </span>интернет-ресурс Оператора kuber.ru, расположенный
                    на&nbsp;доменном имени kuber.ru, включая любые его поддоменные имена, содержащий совокупность
                    графических и&nbsp;информационных материалов, программ для ЭВМ и&nbsp;баз данных, обеспечивающий
                    доступ пользователям к&nbsp;услугам по&nbsp;перевозке контейнеров автомобильным транспортом,
                    а&nbsp;также к&nbsp;иным любым сопутствующим услугам, связанным с&nbsp;перевозками контейнеров.
                  </p>
                  <p>
                    <span className="font-semibold">Персональные данные&nbsp;&mdash;&nbsp;</span>любая информация,
                    относящаяся к&nbsp;прямо или косвенно определенному или определяемому физическому лицу (субъекту
                    персональных данных).
                  </p>
                  <p>
                    <span className="font-semibold">Обработка персональных данных&nbsp;&mdash;&nbsp;</span>любое
                    действие (операция) или совокупность действий (операций), совершаемых с&nbsp;использованием средств
                    автоматизации или без использования таких средств с&nbsp;персональными данными, включая сбор,
                    запись, систематизацию, накопление, хранение, уточнение (обновление, изменение), извлечение,
                    использование, передачу (предоставление, доступ), обезличивание, блокирование, удаление,
                    уничтожение.
                  </p>
                  <p>
                    <span className="font-semibold">Субъект персональных данных</span>&nbsp;или&nbsp;
                    <span className="font-semibold">Субъект</span>&nbsp;при упоминании в&nbsp;единственном числе&nbsp;
                    (а&nbsp;равно&nbsp;<span className="font-semibold">Субъекты персональных данных</span>
                    &nbsp;или&nbsp;
                    <span className="font-semibold">Субъекты</span>&nbsp;при упоминании во&nbsp;множественном
                    числе)&nbsp;&mdash; физическое лицо, в&nbsp;том числе передающее свои Персональные данные Оператору,
                    в&nbsp;том числе пользователь Сайта, которое прямо или косвенно определено или определяемо
                    с&nbsp;помощью Персональных данных.
                  </p>
                  <p>
                    <span className="font-semibold">Пользователь сайта&nbsp;</span>при упоминании в&nbsp;единственном
                    числе (а&nbsp;равно Пользователи сайта при упоминании во&nbsp;множественном числе)&nbsp;&mdash;
                    физическое лицо, являющееся индивидуальным предпринимателем или имеющее статус самозанятого, либо
                    связанное с&nbsp;деятельностью индивидуального предпринимателя или юридического лица, являющееся
                    Субъектом персональных данных, добровольно зарегистрировавшееся на&nbsp;Сайте и&nbsp;предоставившее
                    необходимые Персональные данные при регистрации.
                  </p>
                  <p>
                    <span className="font-semibold">Администрация сайта&nbsp;&mdash;&nbsp;</span>уполномоченные
                    на&nbsp;управление Сайтом работники Оператора, определяющие состав Персональных данных Пользователей
                    сайта, цели сбора Персональных данных, их&nbsp;обработку и&nbsp;защиту.
                  </p>
                </div>
                <ListItem section="1.3." doubleDigit={true}>
                  Настоящая Политика устанавливает порядок обработки и&nbsp;защиты Персональных данных Субъектов,
                  и&nbsp;действует в&nbsp;отношении всей информации, которую Оператор может получить о&nbsp;Субъектах
                  персональных данных, в&nbsp;том числе о&nbsp;Пользователях во&nbsp;время использования ими Сайта.
                  Настоящая Политика не&nbsp;распространяется на&nbsp;сайты третьих лиц, на&nbsp;которые Пользователи
                  могут перейти с&nbsp;Сайта по&nbsp;внешним веб-ссылкам. Администрация сайта не&nbsp;несет
                  ответственность за&nbsp;контент и&nbsp;действия Пользователей на&nbsp;сайтах третьих лиц.
                </ListItem>
                <ListItem section="1.4." doubleDigit={true}>
                  К Персональным данным Субъектов, которые обрабатывает Оператор, относятся:
                </ListItem>
                <ul className="flex list-disc flex-col gap-3 pl-16">
                  <li>
                    <span className="relative left-2">фамилия, имя, отчество;</span>
                  </li>
                  <li>
                    <span className="relative left-2">дата рождения (день, месяц, год);</span>
                  </li>
                  <li>
                    <span className="relative left-2">номер телефона;</span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      паспортные данные (серия, номер, дата выдачи, кем выдан, номер подразделения);
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">адрес регистрации;</span>
                  </li>
                  <li>
                    <span className="relative left-2">ИНН;</span>
                  </li>
                  <li>
                    <span className="relative left-2">СНИЛС;</span>
                  </li>
                  <li>
                    <span className="relative left-2">водительское удостоверение (серия, номер, дата выдачи);</span>
                  </li>
                  <li>
                    <span className="relative left-2">адрес места жительства;</span>
                  </li>

                  <li>
                    <span className="relative left-2">адрес электронной почты;</span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      место работы (организационно-правовая форма и наименование работодателя);
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      семейное положение (серия, номер, кем выдан, дата выдачи документов о&nbsp;заключении и&nbsp;(или)
                      расторжении брака);
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      образование (серия, номер, кем выдан, дата выдачи документов об&nbsp;образовании);
                    </span>
                  </li>

                  <li>
                    <span className="relative left-2">профессия;</span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      доходы (справка по&nbsp;форме &#8470;&nbsp;182н, справка по&nbsp;форме 2НДФЛ);
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">технические данные, необходимые для функционирования Сайта:</span>
                  </li>
                </ul>
                <ul className="flex list-[circle] flex-col gap-3 pl-24">
                  <li>
                    <span className="relative left-2">номер интернет-протокола (IP-адрес);</span>
                  </li>
                  <li>
                    <span className="relative left-2">географическое положение;</span>
                  </li>
                  <li>
                    <span className="relative left-2">поставщик услуг Интернета;</span>
                  </li>
                  <li>
                    <span className="relative left-2">часовой пояс;</span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      сведения о&nbsp;браузере (в&nbsp;том числе язык интерфейса, размеры экрана, информация
                      о&nbsp;файлах cookies (включены/выключены), информация, содержащаяся в&nbsp;файлах cookies, User
                      Agent браузера и&nbsp;пр.);
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      сведения об&nbsp;операционной системе (в&nbsp;том числе язык системы);
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      сведения о&nbsp;технических устройствах (в&nbsp;том числе размеры и&nbsp;поддерживаемые цвета
                      экрана персонального устройства, поддержка устройством некоторых технологий и&nbsp;протоколов
                      (Retina, WebSocket, HTTP2 и&nbsp;пр.)).
                    </span>
                  </li>
                </ul>
                <div className="flex flex-col gap-3 pl-10 pt-3">
                  <p>
                    Указанные Персональные данные передаются Оператору с&nbsp;использованием средств вычислительной
                    техники в&nbsp;автоматизированном режиме.
                  </p>
                  <p>
                    Все Персональные данные о&nbsp;Субъектах Оператор может получить как от&nbsp;них самих, так
                    и&nbsp;от&nbsp;лиц, уполномоченных на&nbsp;Обработку персональных данных Субъектов, в&nbsp;том числе
                    на&nbsp;их&nbsp;передачу Оператору. Персональные данные Субъектов являются конфиденциальной
                    информацией и&nbsp;не&nbsp;могут быть использованы Оператором, Администрацией сайта или любым иным
                    лицом в&nbsp;личных целях.
                  </p>
                </div>
                <ol className="flex flex-col gap-4 pb-4 pl-10">
                  <ListItem section="1.4.1.">
                    На&nbsp;Сайте используются файлы cookies Пользователей. Использование файлов cookies осуществляется
                    на&nbsp;условиях обработки и&nbsp;защиты персональных данных в&nbsp;соответствии с&nbsp;настоящей
                    Политикой.
                  </ListItem>
                  <ListItem section="1.4.2.">
                    Оператор не&nbsp;обрабатывает специальные категории персональных данных (в&nbsp;соответствии
                    со&nbsp;ст.&nbsp;10&nbsp;Закона о&nbsp;персональных данных) и&nbsp;биометрические персональные
                    данные (в&nbsp;соответствии со&nbsp;ст.&nbsp;11&nbsp;Закона о&nbsp;персональных данных).
                  </ListItem>
                </ol>
                <ListItem section="1.5." doubleDigit={true}>
                  Цели Обработки персональных данных и&nbsp;перечень подлежащих обработке Персональных данных зависят
                  от&nbsp;категории Субъектов.
                </ListItem>
                <ol className="flex flex-col gap-4 pl-10">
                  <ListItem section="1.5.1." semibold={true}>
                    Работники Оператора, бывшие работники, кандидаты на&nbsp;замещение вакантных должностей,
                    родственники работников:
                  </ListItem>
                  <ol className="flex flex-col gap-4 pb-4 tablet:pl-[60px]">
                    <ListItem section="1.5.1.1." className="!gap-1 tablet:!gap-7">
                      Цели Обработки персональных данных: реализация договорных правоотношений с&nbsp;ООО
                      &laquo;КУБЕР&raquo;, предоставление дополнительных гарантий и&nbsp;компенсаций, предусмотренных
                      локальными актами ООО &laquo;КУБЕР&raquo;, а&nbsp;также действующим законодательством&nbsp;РФ.
                    </ListItem>
                    <ListItem section="1.5.1.2." className="!gap-1 tablet:!gap-7">
                      Перечень подлежащих обработке Персональных данных:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pb-4 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">фамилия, имя, отчество;</span>
                      </li>
                      <li>
                        <span className="relative left-2">дата рождения (день, месяц, год);</span>
                      </li>
                      <li>
                        <span className="relative left-2">номер телефона;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          паспортные данные (серия, номер, дата выдачи, кем выдан, номер подразделения);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес регистрации;</span>
                      </li>
                      <li>
                        <span className="relative left-2">ИНН;</span>
                      </li>
                      <li>
                        <span className="relative left-2">СНИЛС;</span>
                      </li>
                      <li>
                        <span className="relative left-2">водительское удостоверение (серия, номер, дата выдачи);</span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес места жительства;</span>
                      </li>

                      <li>
                        <span className="relative left-2">адрес электронной почты;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          место работы (организационно-правовая форма и наименование работодателя);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          семейное положение (серия, номер, кем выдан, дата выдачи документов о&nbsp;заключении
                          и&nbsp;(или) расторжении брака);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          образование (серия, номер, кем выдан, дата выдачи документов об&nbsp;образовании);
                        </span>
                      </li>

                      <li>
                        <span className="relative left-2">профессия;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          доходы (справка по&nbsp;форме &#8470;&nbsp;182н, справка по&nbsp;форме 2НДФЛ);
                        </span>
                      </li>
                    </ul>
                    <ListItem section="1.5.1.3." className="!gap-1 tablet:!gap-7">
                      Действия с Персональными данными:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">сбор;</span>
                      </li>
                      <li>
                        <span className="relative left-2">запись;</span>
                      </li>
                      <li>
                        <span className="relative left-2">систематизация;</span>
                      </li>
                      <li>
                        <span className="relative left-2">накопление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">хранение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уточнение (обновление, изменение);</span>
                      </li>
                      <li>
                        <span className="relative left-2">извлечение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">использование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">передача (предоставление, доступ);</span>
                      </li>

                      <li>
                        <span className="relative left-2">обезличивание;</span>
                      </li>
                      <li>
                        <span className="relative left-2">блокирование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">удаление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уничтожение.</span>
                      </li>
                    </ul>
                  </ol>
                  <ListItem section="1.5.2." semibold={true}>
                    Лица, имеющие договорные правоотношения гражданско-правового характера с&nbsp;Оператором или
                    находящиеся на&nbsp;этапе преддоговорных отношений подобного характера:
                  </ListItem>
                  <ol className="flex flex-col gap-4 pb-4 tablet:pl-[60px] ">
                    <ListItem section="1.5.2.1." className="!gap-1 tablet:!gap-7">
                      Цели Обработки персональных данных: реализация договорных правоотношений с&nbsp;ООО
                      &laquo;КУБЕР&raquo;, предоставление дополнительных гарантий и&nbsp;компенсаций, предусмотренных
                      локальными актами ООО &laquo;КУБЕР&raquo;, а&nbsp;также действующим законодательством&nbsp;РФ.
                    </ListItem>
                    <ListItem section="1.5.2.2." className="!gap-1 tablet:!gap-7">
                      Перечень подлежащих обработке Персональных данных:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pb-4 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">фамилия, имя, отчество;</span>
                      </li>
                      <li>
                        <span className="relative left-2">дата рождения (день, месяц, год);</span>
                      </li>
                      <li>
                        <span className="relative left-2">номер телефона;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          паспортные данные (серия, номер, дата выдачи, кем выдан, номер подразделения);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес регистрации;</span>
                      </li>
                      <li>
                        <span className="relative left-2">ИНН;</span>
                      </li>
                      <li>
                        <span className="relative left-2">СНИЛС;</span>
                      </li>
                      <li>
                        <span className="relative left-2">водительское удостоверение (серия, номер, дата выдачи);</span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес места жительства;</span>
                      </li>

                      <li>
                        <span className="relative left-2">адрес электронной почты;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          место работы (организационно-правовая форма и наименование работодателя);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          семейное положение (серия, номер, кем выдан, дата выдачи документов о&nbsp;заключении
                          и&nbsp;(или) расторжении брака);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          образование (серия, номер, кем выдан, дата выдачи документов об&nbsp;образовании);
                        </span>
                      </li>

                      <li>
                        <span className="relative left-2">профессия;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          доходы (справка по&nbsp;форме &#8470;&nbsp;182н, справка по&nbsp;форме 2НДФЛ);
                        </span>
                      </li>
                    </ul>
                    <ListItem section="1.5.2.3." className="!gap-1 tablet:!gap-7">
                      Действия с Персональными данными:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">сбор;</span>
                      </li>
                      <li>
                        <span className="relative left-2">запись;</span>
                      </li>
                      <li>
                        <span className="relative left-2">систематизация;</span>
                      </li>
                      <li>
                        <span className="relative left-2">накопление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">хранение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уточнение (обновление, изменение);</span>
                      </li>
                      <li>
                        <span className="relative left-2">извлечение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">использование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">передача (предоставление, доступ);</span>
                      </li>

                      <li>
                        <span className="relative left-2">обезличивание;</span>
                      </li>
                      <li>
                        <span className="relative left-2">блокирование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">удаление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уничтожение.</span>
                      </li>
                    </ul>
                  </ol>
                  <ListItem section="1.5.3." semibold={true}>
                    Лица, проходящие у&nbsp;Оператора различного рода практику или стажировку.
                  </ListItem>
                  <ol className="flex flex-col gap-4 pb-4 tablet:pl-[60px]">
                    <ListItem section="1.5.3.1." className="!gap-1 tablet:!gap-7">
                      Цели Обработки персональных данных: реализация договорных правоотношений с&nbsp;ООО
                      &laquo;КУБЕР&raquo;, предоставление дополнительных гарантий и&nbsp;компенсаций, предусмотренных
                      локальными актами ООО &laquo;КУБЕР&raquo;, а&nbsp;также действующим законодательством&nbsp;РФ.
                    </ListItem>
                    <ListItem section="1.5.3.2." className="!gap-1 tablet:!gap-7">
                      Перечень подлежащих обработке Персональных данных:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pb-4 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">фамилия, имя, отчество;</span>
                      </li>
                      <li>
                        <span className="relative left-2">дата рождения (день, месяц, год);</span>
                      </li>
                      <li>
                        <span className="relative left-2">номер телефона;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          паспортные данные (серия, номер, дата выдачи, кем выдан, номер подразделения);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес регистрации;</span>
                      </li>
                      <li>
                        <span className="relative left-2">ИНН;</span>
                      </li>
                      <li>
                        <span className="relative left-2">СНИЛС;</span>
                      </li>
                      <li>
                        <span className="relative left-2">водительское удостоверение (серия, номер, дата выдачи);</span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес места жительства;</span>
                      </li>

                      <li>
                        <span className="relative left-2">адрес электронной почты;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          место работы (организационно-правовая форма и наименование работодателя);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          семейное положение (серия, номер, кем выдан, дата выдачи документов о&nbsp;заключении
                          и&nbsp;(или) расторжении брака);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          образование (серия, номер, кем выдан, дата выдачи документов об&nbsp;образовании);
                        </span>
                      </li>

                      <li>
                        <span className="relative left-2">профессия;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          доходы (справка по&nbsp;форме &#8470;&nbsp;182н, справка по&nbsp;форме 2НДФЛ);
                        </span>
                      </li>
                    </ul>
                    <ListItem section="1.5.3.3." className="!gap-1 tablet:!gap-7">
                      Действия с Персональными данными:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">сбор;</span>
                      </li>
                      <li>
                        <span className="relative left-2">запись;</span>
                      </li>
                      <li>
                        <span className="relative left-2">систематизация;</span>
                      </li>
                      <li>
                        <span className="relative left-2">накопление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">хранение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уточнение (обновление, изменение);</span>
                      </li>
                      <li>
                        <span className="relative left-2">извлечение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">использование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">передача (предоставление, доступ);</span>
                      </li>

                      <li>
                        <span className="relative left-2">обезличивание;</span>
                      </li>
                      <li>
                        <span className="relative left-2">блокирование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">удаление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уничтожение.</span>
                      </li>
                    </ul>
                  </ol>
                  <ListItem section="1.5.4." semibold={true}>
                    Пользователи Сайта&nbsp;&mdash; контрагенты Оператора, в&nbsp;том числе индивидуальные
                    предприниматели, самозанятые и&nbsp;иные физические лица.
                  </ListItem>
                  <ol className="flex flex-col gap-4 pb-4 tablet:pl-[60px] ">
                    <ListItem section="1.5.4.1." className="!gap-1 tablet:!gap-7">
                      Цели Обработки персональных данных: установление и&nbsp;реализация договорных правоотношений
                      с&nbsp;ООО &laquo;КУБЕР&raquo;, выполнение требований действующего законодательства&nbsp;РФ.
                    </ListItem>
                    <ListItem section="1.5.4.2." className="!gap-1 tablet:!gap-7">
                      Перечень подлежащих обработке Персональных данных:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">фамилия, имя, отчество;</span>
                      </li>
                      <li>
                        <span className="relative left-2">дата рождения (день, месяц, год);</span>
                      </li>
                      <li>
                        <span className="relative left-2">номер телефона;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          паспортные данные (серия, номер, дата выдачи, кем выдан, номер подразделения);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес регистрации;</span>
                      </li>
                      <li>
                        <span className="relative left-2">ИНН;</span>
                      </li>
                      <li>
                        <span className="relative left-2">СНИЛС;</span>
                      </li>
                      <li>
                        <span className="relative left-2">водительское удостоверение (серия, номер, дата выдачи);</span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес места жительства;</span>
                      </li>

                      <li>
                        <span className="relative left-2">адрес электронной почты;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          место работы (организационно-правовая форма и наименование работодателя);
                        </span>
                      </li>

                      <li>
                        <span className="relative left-2">
                          технические данные, необходимые для функционирования Сайта:
                        </span>
                      </li>
                    </ul>
                    <ul className="flex list-[circle] flex-col gap-3 pb-4 pl-[112px] tablet:pl-[136px]">
                      <li>
                        <span className="relative left-2">номер интернет-протокола (IP-адрес);</span>
                      </li>
                      <li>
                        <span className="relative left-2">географическое положение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">поставщик услуг Интернета;</span>
                      </li>
                      <li>
                        <span className="relative left-2">часовой пояс;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          сведения о&nbsp;браузере (в&nbsp;том числе язык интерфейса, размеры экрана, информация
                          о&nbsp;файлах cookies (включены/выключены), информация, содержащаяся в&nbsp;файлах cookies,
                          User Agent браузера и&nbsp;пр.);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          сведения об&nbsp;операционной системе (в&nbsp;том числе язык системы);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          сведения о&nbsp;технических устройствах (в&nbsp;том числе размеры и&nbsp;поддерживаемые цвета
                          экрана персонального устройства, поддержка устройством некоторых технологий и&nbsp;протоколов
                          (Retina, WebSocket, HTTP2 и&nbsp;пр.)).
                        </span>
                      </li>
                    </ul>
                    <ListItem section="1.5.4.3." className="!gap-1 tablet:!gap-7">
                      Действия с Персональными данными:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">сбор;</span>
                      </li>
                      <li>
                        <span className="relative left-2">запись;</span>
                      </li>
                      <li>
                        <span className="relative left-2">систематизация;</span>
                      </li>
                      <li>
                        <span className="relative left-2">накопление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">хранение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уточнение (обновление, изменение);</span>
                      </li>
                      <li>
                        <span className="relative left-2">извлечение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">использование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">передача (предоставление, доступ);</span>
                      </li>

                      <li>
                        <span className="relative left-2">обезличивание;</span>
                      </li>
                      <li>
                        <span className="relative left-2">блокирование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">удаление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уничтожение.</span>
                      </li>
                    </ul>
                  </ol>
                  <ListItem section="1.5.5." semibold={true}>
                    Пользователи Сайта&nbsp;&mdash; представители&nbsp;и (или) работники контрагентов Оператора
                    (юридических лиц и&nbsp;индивидуальных предпринимателей).
                  </ListItem>
                  <ol className="flex flex-col gap-4 pb-4 tablet:pl-[60px]">
                    <ListItem section="1.5.5.1." className="!gap-1 tablet:!gap-7">
                      Цели Обработки персональных данных: установление и&nbsp;реализация договорных правоотношений
                      с&nbsp;ООО &laquo;КУБЕР&raquo;, выполнение требований действующего законодательства&nbsp;РФ.
                    </ListItem>
                    <ListItem section="1.5.5.2." className="!gap-1 tablet:!gap-7">
                      Перечень подлежащих обработке Персональных данных:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">фамилия, имя, отчество;</span>
                      </li>
                      <li>
                        <span className="relative left-2">дата рождения (день, месяц, год);</span>
                      </li>
                      <li>
                        <span className="relative left-2">номер телефона;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          паспортные данные (серия, номер, дата выдачи, кем выдан, номер подразделения);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес регистрации;</span>
                      </li>
                      <li>
                        <span className="relative left-2">ИНН;</span>
                      </li>
                      <li>
                        <span className="relative left-2">СНИЛС;</span>
                      </li>
                      <li>
                        <span className="relative left-2">водительское удостоверение (серия, номер, дата выдачи);</span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес места жительства;</span>
                      </li>

                      <li>
                        <span className="relative left-2">адрес электронной почты;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          место работы (организационно-правовая форма и наименование работодателя);
                        </span>
                      </li>

                      <li>
                        <span className="relative left-2">
                          технические данные, необходимые для функционирования Сайта:
                        </span>
                      </li>
                    </ul>
                    <ul className="flex list-[circle] flex-col gap-3 pb-4 pl-[112px] tablet:pl-[136px]">
                      <li>
                        <span className="relative left-2">номер интернет-протокола (IP-адрес);</span>
                      </li>
                      <li>
                        <span className="relative left-2">географическое положение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">поставщик услуг Интернета;</span>
                      </li>
                      <li>
                        <span className="relative left-2">часовой пояс;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          сведения о&nbsp;браузере (в&nbsp;том числе язык интерфейса, размеры экрана, информация
                          о&nbsp;файлах cookies (включены/выключены), информация, содержащаяся в&nbsp;файлах cookies,
                          User Agent браузера и&nbsp;пр.);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          сведения об&nbsp;операционной системе (в&nbsp;том числе язык системы);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          сведения о&nbsp;технических устройствах (в&nbsp;том числе размеры и&nbsp;поддерживаемые цвета
                          экрана персонального устройства, поддержка устройством некоторых технологий и&nbsp;протоколов
                          (Retina, WebSocket, HTTP2 и&nbsp;пр.)).
                        </span>
                      </li>
                    </ul>
                    <ListItem section="1.5.5.3." className="!gap-1 tablet:!gap-7">
                      Действия с Персональными данными:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">сбор;</span>
                      </li>
                      <li>
                        <span className="relative left-2">запись;</span>
                      </li>
                      <li>
                        <span className="relative left-2">систематизация;</span>
                      </li>
                      <li>
                        <span className="relative left-2">накопление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">хранение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уточнение (обновление, изменение);</span>
                      </li>
                      <li>
                        <span className="relative left-2">извлечение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">использование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">передача (предоставление, доступ);</span>
                      </li>

                      <li>
                        <span className="relative left-2">обезличивание;</span>
                      </li>
                      <li>
                        <span className="relative left-2">блокирование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">удаление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уничтожение.</span>
                      </li>
                    </ul>
                  </ol>
                  <ListItem section="1.5.6." semibold={true}>
                    Лица, не&nbsp;являющиеся Пользователями сайта, чьи Персональные данные передаются Оператору
                    уполномоченными представителями&nbsp;и (или) работниками контрагентов Оператора.
                  </ListItem>
                  <ol className="flex flex-col gap-4 pb-4 tablet:pl-[60px]">
                    <ListItem section="1.5.6.1." className="!gap-1 tablet:!gap-7">
                      Цели Обработки персональных данных: установление и&nbsp;реализация договорных правоотношений
                      с&nbsp;ООО &laquo;КУБЕР&raquo;, выполнение требований действующего законодательства&nbsp;РФ.
                    </ListItem>
                    <ListItem section="1.5.6.2." className="!gap-1 tablet:!gap-7">
                      Перечень подлежащих обработке Персональных данных:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">фамилия, имя, отчество;</span>
                      </li>
                      <li>
                        <span className="relative left-2">дата рождения (день, месяц, год);</span>
                      </li>
                      <li>
                        <span className="relative left-2">номер телефона;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          паспортные данные (серия, номер, дата выдачи, кем выдан, номер подразделения);
                        </span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес регистрации;</span>
                      </li>
                      <li>
                        <span className="relative left-2">ИНН;</span>
                      </li>
                      <li>
                        <span className="relative left-2">СНИЛС;</span>
                      </li>
                      <li>
                        <span className="relative left-2">водительское удостоверение (серия, номер, дата выдачи);</span>
                      </li>
                      <li>
                        <span className="relative left-2">адрес места жительства;</span>
                      </li>

                      <li>
                        <span className="relative left-2">адрес электронной почты;</span>
                      </li>
                      <li>
                        <span className="relative left-2">
                          место работы (организационно-правовая форма и наименование работодателя);
                        </span>
                      </li>
                    </ul>

                    <ListItem section="1.5.6.3." className="!gap-1 tablet:!gap-7">
                      Действия с Персональными данными:
                    </ListItem>
                    <ul className="flex list-disc flex-col gap-3 pl-[80px] tablet:pl-[104px]">
                      <li>
                        <span className="relative left-2">сбор;</span>
                      </li>
                      <li>
                        <span className="relative left-2">запись;</span>
                      </li>
                      <li>
                        <span className="relative left-2">систематизация;</span>
                      </li>
                      <li>
                        <span className="relative left-2">накопление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">хранение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уточнение (обновление, изменение);</span>
                      </li>
                      <li>
                        <span className="relative left-2">извлечение;</span>
                      </li>
                      <li>
                        <span className="relative left-2">использование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">передача (предоставление, доступ);</span>
                      </li>

                      <li>
                        <span className="relative left-2">обезличивание;</span>
                      </li>
                      <li>
                        <span className="relative left-2">блокирование;</span>
                      </li>
                      <li>
                        <span className="relative left-2">удаление;</span>
                      </li>
                      <li>
                        <span className="relative left-2">уничтожение.</span>
                      </li>
                    </ul>
                  </ol>
                </ol>
                <ListItem section="1.6." doubleDigit={true}>
                  Оператор обеспечивает Субъектам свободный бесплатный доступ к&nbsp;своим Персональным данным, включая
                  право на&nbsp;получение копий любой записи, содержащей их&nbsp;Персональные данные,
                  за&nbsp;исключением случаев, предусмотренных действующим законодательством&nbsp;РФ.
                </ListItem>
                <ListItem section="1.7." doubleDigit={true}>
                  Оператор разрабатывает меры защиты Персональных данных Субъектов, в&nbsp;том числе Пользователей
                  сайта.
                </ListItem>
                <ListItem section="1.8." doubleDigit={true}>
                  Регистрируясь на&nbsp;Сайте и&nbsp;используя Сайт, Пользователь сайта выражает свое согласие
                  с&nbsp;условиями настоящей Политики.
                </ListItem>
                <ListItem section="1.9." doubleDigit={true}>
                  В&nbsp;случае несогласия Пользователя с&nbsp;условиями настоящей Политики, использование Сайта
                  и&nbsp;его сервисов должно быть немедленно прекращено.
                </ListItem>
                <ListItem section="1.10." className="!gap-1">
                  Правовыми основаниями обработки и&nbsp;защиты Персональных данных в&nbsp;соответствии с&nbsp;условиями
                  настоящей Политики являются действующие нормативные правовые акты&nbsp;РФ в&nbsp;области Персональных
                  данных и&nbsp;принятые на&nbsp;их&nbsp;основе правовые акты, внутренние документы Оператора, договоры,
                  стороной которых является Оператор, а&nbsp;также иные документы, которые в&nbsp;соответствии
                  с&nbsp;действующим законодательством&nbsp;РФ могут быть отнесены к&nbsp;правовым основаниям обработки
                  персональных данных.
                </ListItem>
              </div>
            </div>
            <div className="flex flex-col gap-4 pb-4" id="conditions">
              <h2 className="text-title1">2. Условия обработки персональных данных</h2>
              <ol className="flex list-inside flex-col gap-4">
                <ListItem section="2.1." doubleDigit={true}>
                  Обработка персональных данных Субъектов осуществляется исключительно в&nbsp;целях, указанных
                  в&nbsp;п.&nbsp;1.5 настоящей Политики.
                </ListItem>
                <ListItem section="2.2." doubleDigit={true}>
                  Обработка персональных данных Субъектов на&nbsp;Сайте осуществляется с&nbsp;использованием средств
                  вычислительной техники в&nbsp;автоматизированном режиме.
                </ListItem>
                <ListItem section="2.3." doubleDigit={true}>
                  Персональные данные Субъектов хранятся в&nbsp;электронном виде в&nbsp;системах Сайта, поддерживаемого
                  техническими службами, расположенными на&nbsp;территории&nbsp;РФ.
                </ListItem>
                <ol className="flex flex-col gap-4 pb-4 pl-10">
                  <ListItem section="2.3.1.">
                    При хранении Персональных данных Субъектов третьими лицами по&nbsp;поручению Оператора принимаются
                    организационные и&nbsp;технические меры, обеспечивающие их&nbsp;сохранность и&nbsp;исключающие
                    несанкционированный доступ к&nbsp;ним.
                  </ListItem>
                  <ListItem section="2.3.2.">
                    К&nbsp;Обработке персональных данных Субъектов могут иметь доступ Оператор и&nbsp;третьи лица,
                    допущенные к&nbsp;работе с&nbsp;Персональными данными в&nbsp;соответствии с&nbsp;Законом
                    о&nbsp;персональных данных, которым на&nbsp;основании договора с&nbsp;Оператором дано поручение
                    на&nbsp;Обработку персональных данных.
                  </ListItem>
                  <ListItem section="2.3.3.">
                    Перечень работников Оператора, имеющих доступ к&nbsp;Персональным данным и&nbsp;уполномоченных
                    обрабатывать Персональные данные Субъектов, утверждается локальными актами Оператора.
                  </ListItem>
                </ol>
                <ListItem section="2.4." doubleDigit={true}>
                  Оператор вправе передавать Персональные данные Субъектов, в&nbsp;том числе Пользователей сайта,
                  третьим лицам в&nbsp;соответствии с&nbsp;условиями настоящей Политики, а&nbsp;также в&nbsp;случаях,
                  установленных действующим законодательством РФ. В&nbsp;случае передачи Персональных данных Оператор
                  передает такие данные только уполномоченным представителям третьих лиц и&nbsp;только в&nbsp;той части,
                  которая необходима для исполнения ими своих обязанностей в&nbsp;соответствии с&nbsp;договорными
                  правоотношениями, участником которых является ООО &laquo;КУБЕР&raquo;, настоящей Политикой
                  и&nbsp;действующим законодательством&nbsp;РФ.
                </ListItem>
                <ListItem section="2.5." doubleDigit={true}>
                  В&nbsp;целях повышения качества работы Сайта и&nbsp;обеспечения возможности правовой защиты Субъектов
                  Администрация сайта вправе хранить лог-файлы о&nbsp;действиях, совершенных Пользователями
                  в&nbsp;рамках использования ими Сайта.
                </ListItem>
                <ListItem section="2.6." doubleDigit={true}>
                  Обработка персональных данных осуществляется в&nbsp;течение всего срока, необходимого для полного
                  достижения целей, указанных в&nbsp;настоящей Политике, для которых они были собраны, а&nbsp;также для
                  обеспечения соблюдения требований действующего законодательства&nbsp;РФ.
                </ListItem>
                <ListItem section="2.7." doubleDigit={true}>
                  В&nbsp;случае передачи Оператору Персональных данных Субъектов, указанных
                  в&nbsp;п.&nbsp;1.5.6&nbsp;Политики, лицо, передающее такие данные, гарантирует соблюдение
                  требований&nbsp;ст.&nbsp;18&nbsp;Закона о&nbsp;персональных данных, в&nbsp;том числе получение
                  согласия от&nbsp;Субъектов на&nbsp;обработку их&nbsp;Персональных данных.
                </ListItem>
                <ListItem section="2.8." doubleDigit={true}>
                  Субъекты, чьи Персональные данные переданы Оператору, считаются уведомленными о&nbsp;факте такой
                  передачи и&nbsp;обработки их&nbsp;Персональных данных Оператором, в&nbsp;силу чего обязанность,
                  предусмотренная&nbsp;п.&nbsp;3&nbsp;ст.&nbsp;18&nbsp;Закона о&nbsp;персональных данных,
                  не&nbsp;требует исполнения со&nbsp;стороны Оператора.
                </ListItem>
                <ListItem section="2.9." doubleDigit={true}>
                  Оператор не&nbsp;осуществляет проверку достоверности полученных Персональных данных Субъектов,
                  полагая, что лица, передающие такие данные Оператору, действуют добросовестно, поддерживают
                  актуальность Персональных данных, имеют необходимые согласия на&nbsp;обработку Персональных данных
                  от&nbsp;соответствующих Субъектов, а&nbsp;также исполняют иные обязанности, предусмотренные
                  действующим законодательством&nbsp;РФ.
                </ListItem>
              </ol>
            </div>
            <div className="flex flex-col gap-4" id="equipment">
              <h2 className="text-title1">
                3. Оборудование и&nbsp;помещения, используемые для обработки персональных данных
              </h2>
              <ol className="flex list-inside flex-col gap-4">
                <ListItem section="3.1." doubleDigit={true}>
                  Технические средства систем Сайта и&nbsp;средства защиты информации Оператора обеспечивают сохранность
                  обрабатываемых Персональных данных и&nbsp;исключают несанкционированный доступ к&nbsp;ним посторонних
                  лиц.
                </ListItem>
                <ListItem section="3.2." doubleDigit={true}>
                  Помещения, в&nbsp;которых располагаются технические средства систем Сайта, в&nbsp;том числе носители
                  Персональных данных, соответствуют требованиям технической безопасности, установленным действующим
                  законодательством&nbsp;РФ.
                </ListItem>
              </ol>
            </div>

            <div className="flex flex-col gap-4" id="operator-rights">
              <h2 className="text-title1">4. Права, обязанности и&nbsp;ответственность оператора</h2>
              <ol className="flex list-inside flex-col gap-4">
                <ListItem section="4.1." doubleDigit={true}>
                  Оператор вправе устанавливать требования к&nbsp;составу Персональных данных Субъектов, которые должны
                  обязательно предоставляться в&nbsp;том числе для использования Сайта, при этом Оператор
                  руководствуется настоящей Политикой, Законом о&nbsp;персональных данных, Конституцией&nbsp;РФ, иными
                  нормативными правовыми актами&nbsp;РФ.
                </ListItem>
                <ListItem section="4.2." doubleDigit={true}>
                  Оператор за&nbsp;свой счет обеспечивает защиту Персональных данных Субъектов от&nbsp;неправомерного
                  использования или утраты в&nbsp;порядке, установленном действующим законодательством&nbsp;РФ.
                </ListItem>
                <ListItem section="4.3." doubleDigit={true}>
                  Оператор принимает меры, необходимые и&nbsp;достаточные для обеспечения выполнения
                  им&nbsp;обязанностей, предусмотренных Законом о&nbsp;персональных данных и&nbsp;принятыми
                  в&nbsp;соответствии с&nbsp;ним нормативными правовыми актами, а&nbsp;также самостоятельно определяет
                  состав и&nbsp;перечень таких мер.
                </ListItem>
                <ListItem section="4.4." doubleDigit={true}>
                  Оператор не&nbsp;несет ответственности за&nbsp;добровольную передачу Пользователями сайта своих
                  Персональных данных, в&nbsp;том числе логина и&nbsp;пароля от&nbsp;личного кабинета на&nbsp;Сайте,
                  третьим лицам.
                </ListItem>
              </ol>
            </div>
            <div className="flex flex-col gap-4" id="subject-rights">
              <h2 className="text-title1">5. Права субъекта на&nbsp;защиту персональных данных</h2>
              <ol className="flex list-inside flex-col gap-4">
                <ListItem section="5.1." doubleDigit={true}>
                  Субъект в&nbsp;целях обеспечения защиты своих Персональных данных имеет право:
                </ListItem>
                <ul className="flex list-disc flex-col gap-3 pb-4 pl-16">
                  <li>
                    <span className="relative left-2">
                      получать полную информацию о&nbsp;своих Персональных данных, их&nbsp;обработке, хранении
                      и&nbsp;передаче;
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      назначать представителей для защиты своих Персональных данных;
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      требовать исключения или исправления некорректных или неполных персональных данных, а&nbsp;также
                      данных, обработанных с&nbsp;нарушениями настоящей Политики и&nbsp;законодательства&nbsp;РФ;
                    </span>
                  </li>
                  <li>
                    <span className="relative left-2">
                      требовать от&nbsp;Оператора извещения всех лиц, которым ранее были сообщены их&nbsp;некорректные
                      или неполные персональные данные, обо всех произведенных в&nbsp;них исключениях, исправлениях или
                      дополнениях.
                    </span>
                  </li>
                </ul>
                <ListItem section="5.2." doubleDigit={true}>
                  При отказе Оператора исключить или исправить Персональные данные, Субъект или его представитель вправе
                  заявить в&nbsp;письменном виде о&nbsp;своем несогласии с&nbsp;действиями Оператора, предоставив
                  соответствующее обоснование.
                </ListItem>
                <ListItem section="5.3." doubleDigit={true}>
                  Субъект вправе лично или через своего представителя в&nbsp;любой момент отредактировать персональные
                  данные, предоставленные Оператору на&nbsp;Сайте.
                </ListItem>
                <ListItem section="5.4." doubleDigit={true}>
                  Субъект вправе самостоятельно ограничить сбор информации третьими лицами, используя стандартные
                  настройки конфиденциальности применяемого им&nbsp;для работы с&nbsp;Сайтом браузера, а&nbsp;также
                  в&nbsp;любое время изменить, удалить или дополнить предоставленные им&nbsp;персональные данные.
                </ListItem>
                <ListItem section="5.5." doubleDigit={true}>
                  Если Субъект считает, что обработка его Персональных данных осуществляется с&nbsp;нарушением
                  требований Закона о&nbsp;персональных данных или иным образом нарушает его права и&nbsp;свободы,
                  он&nbsp;вправе обжаловать действия или бездействие Оператора в&nbsp;уполномоченный орган
                  по&nbsp;защите прав Субъектов Персональных данных или в&nbsp;судебном порядке.
                </ListItem>
              </ol>
            </div>
            <div className="flex flex-col gap-4" id="data-manipulations">
              <h2 className="text-title1">6. Порядок блокирования, удаления и&nbsp;уничтожения персональных данных</h2>
              <ol className="flex list-inside flex-col gap-4">
                <ListItem section="6.1." doubleDigit={true}>
                  Блокирование, удаление, уничтожение Персональных данных Субъектов производятся Оператором
                  в&nbsp;соответствии с&nbsp;требованиями Закона о&nbsp;персональных данных, а&nbsp;также иными
                  требованиями действующего законодательства&nbsp;РФ.
                </ListItem>
              </ol>
            </div>
            <div className="flex flex-col gap-4" id="responsibility">
              <h2 className="text-title1">
                7. Ответственность за нарушения в области обработки и защиты персональных данных
              </h2>
              <ol className="flex list-inside flex-col gap-4">
                <ListItem section="7.1." doubleDigit={true}>
                  Лица, виновные в&nbsp;нарушении норм, регулирующих обработку и&nbsp;защиту Персональных данных
                  Субъектов, привлекаются к&nbsp;дисциплинарной, материальной, гражданско-правовой, административной
                  и&nbsp;уголовной ответственности в&nbsp;порядке, установленном действующим законодательством&nbsp;РФ.
                </ListItem>
                <ListItem section="7.2." doubleDigit={true}>
                  Моральный вред, причиненный Субъекту вследствие нарушения его прав в&nbsp;области обработки
                  и&nbsp;защиты персональных данных, подлежит возмещению в&nbsp;соответствии с&nbsp;действующим
                  законодательством РФ. Возмещение морального вреда осуществляется независимо от&nbsp;возмещения
                  материального вреда, причиненного Субъекту.
                </ListItem>
                <ListItem section="7.3." doubleDigit={true}>
                  В&nbsp;случае, если лица, передающие Оператору персональные данные Субъектов, нарушают
                  требования&nbsp;п.&nbsp;2.7&nbsp;Политики, Оператор не&nbsp;несет ответственность за&nbsp;обработку
                  Персональных данных, полученных от&nbsp;таких лиц.
                </ListItem>
              </ol>
            </div>
            <div className="flex flex-col gap-4" id="policy-changes">
              <h2 className="text-title1">8. Изменение политики</h2>
              <ol className="flex list-inside flex-col gap-4">
                <ListItem section="8.1." doubleDigit={true}>
                  Настоящая Политика может быть изменена или прекращена Оператором в&nbsp;одностороннем порядке без
                  предварительного уведомления Субъектов персональных данных. Новая редакция Политики в&nbsp;случае
                  её&nbsp;изменения вступает в&nbsp;силу с&nbsp;момента её&nbsp;размещения на&nbsp;Сайте, если иное
                  не&nbsp;предусмотрено новой редакцией Политики.
                </ListItem>
                <ListItem section="8.2." doubleDigit={true}>
                  Действующая редакция Политики находится на&nbsp;Сайте в&nbsp;сети Интернет по&nbsp;адресу:&nbsp;
                  <a href="/" className="cursor-pointer underline underline-offset-[3px]">
                    kuber.ru
                  </a>
                  .
                </ListItem>
                <ListItem section="8.3." doubleDigit={true}>
                  С&nbsp;целью получения обратной связи по&nbsp;вопросам обработки и&nbsp;защиты Персональных данных ООО
                  &laquo;КУБЕР&raquo; предусмотрена форма обратной связи, размещенная на&nbsp;Сайте. Данная форма может
                  быть использована в&nbsp;том числе для направления запросов о&nbsp;реализации прав Субъектов, жалоб
                  на&nbsp;некорректность Персональных данных или неправомерность их&nbsp;обработки.
                </ListItem>
              </ol>
            </div>
          </div>
          <div className={clsx("relative hidden h-[588px] w-[397px] pl-10 desktop:flex desktop:basis-1/3")}>
            <div
              className={clsx("fixed flex flex-col gap-2 transition-all", {
                "top-[332px]": scrollY >= 0 && scrollY < scrollYAfterHeader,
                "top-[120px]": scrollY > 0,
                "opacity-0": scrollY > scrollYBeforeFooter,
              })}
            >
              <h3 className="text-textMediumSemi">Оглавление</h3>
              <NavigationItem item={1} paragraphs={["Общие положения"]} href="#general" />

              <NavigationItem item={2} paragraphs={["Условия обработки персональных", "данных"]} href="#conditions" />
              <NavigationItem
                item={3}
                paragraphs={["Оборудование и помещения,", "используемые для обработки", "персональных данных"]}
                href="#equipment"
              />

              <NavigationItem
                item={4}
                paragraphs={["Права, обязанности и ответственность", "оператора"]}
                href="#operator-rights"
              />
              <NavigationItem
                item={5}
                paragraphs={["Права субъекта на защиту", "персональных данных"]}
                href="#subject-rights"
              />

              <NavigationItem
                item={6}
                paragraphs={["Порядок блокирования, удаления", "и уничтожения персональных данных"]}
                href="#data-manipulations"
              />
              <NavigationItem
                item={7}
                paragraphs={["Ответственность за нарушения в области", "обработки и защиты персональных", "данных"]}
                href="#responsibility"
              />

              <NavigationItem item={8} paragraphs={["Изменение политики"]} href="#policy-changes" />
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default PrivacyPolicyPage;

const ListItem: React.FC<
  HTMLAttributes<HTMLLIElement> & {
  section: string;
  semibold?: boolean;
  doubleDigit?: boolean;
}
> = ({ className, section, semibold, doubleDigit, children, ...props }) => (
  <li
    className={clsx("flex flex-row gap-5", className, {
      "font-semibold": semibold,
      "!gap-3": doubleDigit,
    })}
    {...props}
  >
    <span>{section}</span>
    <span>{children}</span>
  </li>
);

const NavigationItem: React.FC<
  HTMLAttributes<HTMLDivElement> & {
  paragraphs: string[];
  item: number;
  href: string;
}
> = ({ className, paragraphs, item, href, children, ...props }) => (
  <div className="flex flex-row" {...props}>
    {item}.&nbsp;&nbsp;&nbsp;
    <Link href={href}>
      <div className="flex cursor-pointer flex-col underline underline-offset-[3px]">
        {paragraphs.map((item, index) => (
          <p key={index}>{item}</p>
        ))}
      </div>
    </Link>
  </div>
);
